import React, { useState, useEffect } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { useSnackbar } from 'notistack';
import { useConnectModal, useChainModal } from '@rainbow-me/rainbowkit';
import { useAccount, useChainId } from 'wagmi';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';

import StakeForm from './stake';
import ReferralForm from './referral';
import ProfileForm from './profile';
import FaqForm from './faq';
import Banner from './banner';
import { fetchStakeData, fetchUserData } from '../../helpers/get-stake-data';
import { approveStake } from '../../helpers/approve-stake';
import { stake } from '../../helpers/stake';
import { unstake, claimBonus, claimReward } from '../../helpers/unstake';

const StakingDashboard = ({ }) => {

  const chainId = useChainId();
  const { openChainModal } = useChainModal();
  const { openConnectModal } = useConnectModal();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [isFetching, setIsFetching] = useState(false);
  const { address } = useAccount();

  const [stakeData, setStakeData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(false);
  const [supportedNetwork, setSupportedNetwork] = useState(8453);
  const [stakeButton, setStakeButton] = useState("CONNECT WALLET");
  const [stakedAmount, setStakedAmount] = useState(0);
  const [unstakeButton, setUnstakeButton] = useState("UNSTAKE");
  const [claimButton, setClaimButton] = useState("CONNECT WALLET");

  const handleSetNetwork = async () => {
    if (address) {
      // Base mainnet // 8453
      if (chainId != supportedNetwork) {
        setErrorMessage("Staking is only available on the Base network. Please switch your network to Base.");
        openChainModal();
      } else {
        setErrorMessage("");
      }
    }
  };

  const handleGetUserData = async () => {
    if (chainId == supportedNetwork) {
      const userDataResponse = await fetchUserData(address);

      if (userDataResponse.success) {
        setUserData(userDataResponse);
        setStakedAmount(userDataResponse.stakedAmount);
        setStakeButton("STAKE")
        setClaimButton("CLAIM")
      } else {
        snackMessage(userDataResponse.message ? userDataResponse.message : "Something went wrong", 'warning')
      }
      setIsFetching(false);
      return userDataResponse;
    } else {
      setIsFetching(false);
      return null;
    }

  }

  const fetchData = async () => {
    setIsFetching(true);
    const stakeDataResponse = await fetchStakeData();
    if (stakeDataResponse.success == true) {
      setStakeData(stakeDataResponse);
    }
    setIsFetching(false);
  };


  const handleConfirmStake = async (_amount, _referrer) => { 
    _referrer = !_referrer.length ? 0 : _referrer;

    const stakeResponse = await stake(_amount, _referrer);
    if (stakeResponse.success) {
      snackMessage(stakeResponse.message, "success");
      setIsFetching(true);
      await handleGetUserData()
      await fetchData();
      setIsFetching(false);
    } else {
      setStakeButton("STAKE");
      snackMessage(stakeResponse.message, "error")
    }
  }

  const handleStake = async (amount, referrer) => {
    if (!stakeData) return;
    if (!address) {
      openConnectModal();
    } else {
      await handleGetUserData();
      if (parseFloat(amount) < parseInt(stakeData.minStakeAmount)) {
        snackMessage(`The minimum stake amount is ${stakeData.minStakeAmount} OSCAR.`, 'warning');
        return;
      }
      if (userData) {
        if (parseFloat(userData.hopeBalance) < parseFloat(amount)) {
          snackMessage("Insufficient balance. Please ensure your wallet has enough OSCAR to cover the entered stake amount.", "warning");
          return;
        }
        if (parseFloat(userData.stakeAllowance) < amount) {
          setStakeButton("APPROVING...")
          const approveResponse = await approveStake(amount);
          if (approveResponse.success) {
            snackMessage(approveResponse.message, "success");
            setTimeout(() => {
              handleConfirmStake(amount, referrer);
            }, 3000);
          } else {
            setStakeButton("STAKE");
            snackMessage(approveResponse.message, "error")
          }

        } else {
          handleConfirmStake(amount, referrer);
        }
      } else {
        snackMessage("Insufficient balance. Please ensure your wallet has enough OSCAR to cover the entered stake amount.", "warning");
        return;
      }
    }
  }


  const handleClaimReward = async () => {
    if (!stakeData || !userData) return;
    if (userData.pendingReward == 0) {
      snackMessage(`You do not have any rewards available to claim at the moment`, "warning");
      handleUpdateButtonName('CLAIM');
      return;
    }
    setClaimButton("CONFIRMING...")
    const claimResponse = await claimReward();
    setIsFetching(true);
    await handleGetUserData()
    await fetchData();
    setIsFetching(false);
    if (claimResponse.success) {
      snackMessage(claimResponse.message, "success");
    } else {
      snackMessage(claimResponse.message, "error")
    }
    setClaimButton("CLAIM")
  }

  const handleClaimBonus = async () => {
    if (!address) {
      openConnectModal();
      return;
    }
    if (!stakeData || !userData) return;
    if (userData.bonusDebt == 0) {
      snackMessage(`You do not have any rewards available to claim at the moment`, "warning");
      handleUpdateButtonName('CLAIM');
      return;
    }
    setClaimButton("CONFIRMING...")
    const claimResponse = await claimBonus();
    setIsFetching(true);
    await handleGetUserData()
    await fetchData();
    setIsFetching(false);
    if (claimResponse.success) {
      snackMessage(claimResponse.message, "success");
    } else {
      snackMessage(claimResponse.message, "error")
    }
    setClaimButton("CLAIM")

  }

  const handleUnstake = async () => {
    if (!stakeData || !userData) return;
    if (userData.stakedAmount == 0) {
      snackMessage(`You do not have any staked amount.`, 'warning');
      handleUpdateButtonName('UNSTAKE');
      return;
    }
    setUnstakeButton("CONFIRMING...")
    const claimResponse = await unstake();
    setIsFetching(true);
    await handleGetUserData()
    await fetchData();
    setIsFetching(false);
    if (claimResponse.success) {
      snackMessage(claimResponse.message, "success");
    } else {
      snackMessage(claimResponse.message, "error")
    }
    setUnstakeButton("UNSTAKE")
  }

  const handleUpdateButtonName = (_button) => {
    setTimeout(() => {
      if (_button == 'UNSTAKE') {
        setUnstakeButton('UNSTAKE')
      }
      if (_button == 'CLAIM') {
        setClaimButton('CLAIM')
      }
    }, 3000);
  }


  useEffect(() => {
    if (!stakeData) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    handleSetNetwork();
  }, [chainId]);


  useEffect(() => {
    setIsFetching(true);
    if (address) {
      handleGetUserData();
      setClaimButton("CLAIM");
      setStakeButton("STAKE");
    } else {
      setStakeButton("CONNECT WALLET");
      setClaimButton("CONNECT WALLET");
      setIsFetching(false);
    }

  }, [address]);


  const snackMessage = (message, variant) => {
    enqueueSnackbar(message, { variant });
    setTimeout(() => {
      closeSnackbar();
    }, 5000);
  };





  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));

  const [dataCoinTab] = useState([
    {
      id: 1,
      title: 'Stake',
      icon: 'fa-usd'
    },
    {
      id: 2,
      title: 'Referrals',
      icon: 'fa-share-nodes'
    },
    {
      id: 3,
      title: 'Profile',
      icon: 'fa-user'
    },
    {
      id: 4,
      title: 'FAQ',
      icon: 'fa-question-circle'
    }


  ]);
  return (
    <div>


      <Banner stakeData={stakeData} />


      <section className="wallet buy-crypto flat-tabs">
        <div className="container">
          <div className="row">
            <Tabs>
              <TabList>
                {
                  dataCoinTab.map(idx => (
                    <Tab key={idx.id}>  <i className={`fa ${idx.icon}`}></i> {idx.title}</Tab>
                  ))
                }
              </TabList> 

              <TabPanel>
                <StakeForm
                  stakeButton={stakeButton}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  address={address}
                  stakeData={stakeData}
                  stake={handleStake}
                  handleGetUserData={handleGetUserData}
                  snackMessage={snackMessage}
                  userData={userData}
                  HtmlTooltip={HtmlTooltip}
                  openConnectModal={openConnectModal}
                  unstakeButton={unstakeButton}
                  claimButton={claimButton}
                  unstake={handleUnstake}
                  claimReward={handleClaimReward}
                />
              </TabPanel>



              {/* Referral form */}
              <TabPanel>
                <ReferralForm
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  stakeData={stakeData}
                  handleGetUserData={handleGetUserData}
                  snackMessage={snackMessage}
                  userData={userData}
                  HtmlTooltip={HtmlTooltip}
                  openConnectModal={openConnectModal}
                  claimButton={claimButton}
                  claimReward={handleClaimBonus} />

              </TabPanel>


              {/* Profile Form */}
              <TabPanel>
                <ProfileForm
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  stakeData={stakeData}
                  handleGetUserData={handleGetUserData}
                  userData={userData}
                  snackMessage={snackMessage}
                  HtmlTooltip={HtmlTooltip}
                  openConnectModal={openConnectModal}
                />

              </TabPanel>
              <TabPanel>
                <FaqForm />

              </TabPanel>



            </Tabs>

          </div>
        </div>
      </section>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </div>
  );

};

export default StakingDashboard;
